import clsx from 'clsx'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { ColumnInstance, useTable } from 'react-table'
import { KTCard, KTCardBody, KTSVG } from '../../../_metronic/helpers'
import { IOption } from '../../modules/core'
import { PaginationStd } from './PaginationStd'

export interface PageSetting {
  currentPage: number
  totalRow: number
  pageSize: number
}

export interface RenderParam<T> {
  item: T
  index: number
  opt: any
  source: T[]
  events?:Function[]
}

export interface ColDef<T> {
  label: string
  hidden?: boolean
  renderItem?: (param: RenderParam<T>) => React.ReactNode
  events?:Function[]
  filterOption?: IOption[]
  hasTextFilter?: boolean
  width?: string
  onFilterOptionSelected?: (e: any) => void
}

export interface ToolbarStateHandler {
  forceClearSelected?: {}
}

export interface ToolbarSetting {
  showBtnAdd?: boolean
  showBtnExport?: boolean
  showFilter?: boolean
  showSearch?: boolean
  btnAddText?: string
  searchTextValue?: string
  onBtnAddClick?: (e: any) => void
  onSearchTextChange?: (e: any) => void
  onFilterReset?: (e: any) => void
  onFilterApply?: (val: { label: string; value: any }[]) => void
  onDeleteSelected?: (val: any[]) => void
  onExportClick?: (e: any) => void
  customizeSelectionAction?: (selected: any[], isAllPageSelected?: boolean) => React.ReactNode
  customizeAddButton?:()=>  React.ReactNode
  toolbarState?: ToolbarStateHandler
}
export interface TableSetting<T> {
  showColAction?: boolean
  showSelection?: boolean
  primaryKey: (key: T) => any
  onBtnEditClick?: (e: any, id: any) => void
  onBtnDeleteClick?: (e: any, id: any) => void
  onPageChange?: (e: any, page: number) => void
  options?: any
}
interface Props<T, ColDef> {
  columns: ColDef[]
  footers?: ColDef[]
  data: T[]
  dataFooter?: T[]
  toolbarSetting?: ToolbarSetting
  tableSetting?: TableSetting<T>
  pageSetting?: PageSetting
  isLoading?: boolean
}

const defaultProps = {
  tableSetting: {
    showColAction: true,
    showSelection: false,
  } as TableSetting<any>,
  toolbarSetting: {
    showBtnAdd: true,
    showBtnExport: false,
    showFilter: true,
    showSearch: true,
    btnAddText: 'Add New',
  } as ToolbarSetting,
}

const Filter = <T extends object>(
  props: {
    columns: ColDef<T>[];
    onApply: (val: any[]) => void;
  }) => {
  const [selected, setSelected] = useState<{ label: string; value: any }[]>([])
  const handleSelected = (e: any, label: string) => {
    const state = selected
    const exist = state.findIndex((x) => x.label === label)
    if (exist >= 0) {
      state[exist].value = e.target.value
      setSelected(state)
    } else {
      setSelected([...state, { label: label, value: e.target.value }])
    }
  }



  function findSelected(label: string, value: any) {
    const exist = selected.findIndex((x) => x.label === label && x.value === value)
    return exist >= 0
  }

  const handleApply = (e: any) => {
    props.onApply(selected)
  }

  const handleReset = () => {
    setSelected([])
    props.onApply([])
  }


  const renderFilterOption = (colItem: ColDef<T>) => {
    if (!colItem.filterOption) return false;
    return <div className='mb-10'>
      <label className='form-label fs-6 fw-bold'>{colItem.label}:</label>
      <select
        className='form-select form-select-solid fw-bolder'
        onChange={(e) => handleSelected(e, colItem.label)}
        defaultValue={''}
      >
        <option value=''></option>
        {colItem.filterOption.map((optItem, index) => {
          return (
            <option
              key={index}
              //selected={findSelected(colItem.label, optItem.value)}
              value={optItem.value}
            >
              {optItem.label}
            </option>
          )
        })}
      </select>
    </div>

  }

  const renderFilterText = (colItem: ColDef<T>) => {
    if (!colItem.hasTextFilter) return false;
    return <div className='mb-10'>
      <label className='form-label fs-6 fw-bold'>{colItem.label}:</label>
      <input className='form-control' defaultValue={''} onChange={(e) => handleSelected(e, colItem.label)} />
    </div>
  }



  return (
    <>
      {/* begin::Filter Button */}
      <button
        disabled={false}
        type='button'
        className='btn btn-light-primary me-3'
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
      >
        <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-2' />
        Filter
      </button>
      {/* end::Filter Button */}
      {/* begin::SubMenu */}
      <div className='menu menu-sub menu-sub-dropdown w-300px w-md-325px' data-kt-menu='true'>
        {/* begin::Header */}
        <div className='px-7 py-5'>
          <div className='fs-5 text-dark fw-bolder'>Filter Options</div>
        </div>
        {/* end::Header */}

        {/* begin::Separator */}
        <div className='separator border-gray-200'></div>
        {/* end::Separator */}

        {/* begin::Content */}
        <div className='px-7 py-5' data-kt-user-table-filter='form'>
          {/* begin::Input group */}
          {props.columns.map((colItem, index) => {
            return (<div key={index}>
              {renderFilterOption(colItem)}
              {renderFilterText(colItem)}
            </div>)
          })}
          {/* end::Input group */}

          {/* begin::Actions */}
          <div className='d-flex justify-content-end'>
            <button
              type='button'
              className='btn btn-light btn-active-light-primary fw-bold me-2 px-6'
              data-kt-menu-dismiss='true'
              data-kt-user-table-filter='reset'
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type='button'
              className='btn btn-primary fw-bold px-6'
              data-kt-menu-dismiss='true'
              data-kt-user-table-filter='filter'
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
          {/* end::Actions */}
        </div>
        {/* end::Content */}
      </div>
      {/* end::SubMenu */}
    </>
  )
}
const Toolbar = <T extends object>(props: {
  toolbarSetting: ToolbarSetting
  columns: ColDef<T>[]
  onApply: (val: any[]) => void
  onAdd: (e: any) => void
  customizeAddButton?:()=>React.ReactNode
}) => {

const DefaultButtonAdd=()=>{
  return <button
  type='button'
  className='btn btn-primary'
  onClick={props.onAdd}
>
  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
  {props.toolbarSetting.btnAddText}
</button>
}

  return (
    <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
      {props.toolbarSetting.showFilter && (
        <Filter columns={props.columns} onApply={props.onApply} />
      )}

      {props.toolbarSetting.showBtnExport && (
        <button type='button' className='btn btn-light-primary me-3' onClick={props.toolbarSetting?.onExportClick}>
          <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
          Export
        </button>
      )}

      { props.toolbarSetting.showBtnAdd &&  (props.customizeAddButton  ? props.customizeAddButton?.() :<DefaultButtonAdd/>)}
    </div>
  )
}


const LoadingComponent = () => {
  const styles = {
    borderRadius: '0.475rem',
    boxShadow: '0 0 50px 0 rgb(82 63 105 / 15%)',
    backgroundColor: '#fff',
    color: '#7e8299',
    fontWeight: '500',
    margin: '0',
    width: 'auto',
    padding: '1rem 2rem',
    top: 'calc(50% - 2rem)',
    left: 'calc(50% - 4rem)',
  }

  return <tbody>
    <tr>
      <td> <div style={{ ...styles, position: 'absolute', textAlign: 'center' }}>Processing...</div></td>
    </tr>
  </tbody>
}


const Header = <T extends object>(props: {
  toolbarSetting: ToolbarSetting
  pageSetting?: PageSetting
  columns: ColDef<T>[]
  selected?: any
  selectedAllItemPages?: boolean
  isAllPageSelected: boolean
  onApply: (val: any[]) => void
  onAdd: (e: any) => void
  onSelectedAllItemPages: () => void
  onDeleteSelected?: (val: any[]) => void
}) => {
  const [searchTerm, setSearchTerm] = useState<string>(props.toolbarSetting.searchTextValue || '')
  const searchHandleChange = (e: any) => {
    //e.preventDefault()
    setSearchTerm(e.target.value)

    props.toolbarSetting.onSearchTextChange?.(e)
  }

  const DefaultOnSelectDeleteButton = () => {
    return <button
      type='button'
      className='btn btn-danger'
      onClick={() => props.onDeleteSelected?.(props.selected)}
    >
      Delete Selected
    </button>
  }

  return (
    <>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          {/* begin::Search */}
          {props.toolbarSetting.showSearch && (
            <div className='d-flex align-items-center position-relative my-1'>
              <KTSVG
                path='/media/icons/duotune/general/gen021.svg'
                className='svg-icon-1 position-absolute ms-6'
              />
              <input
                type='text'
                data-kt-user-table-filter='search'
                className='form-control form-control-solid w-250px ps-20'
                placeholder='Search'
                value={searchTerm}
                onChange={(e) => searchHandleChange(e)}
              />
            </div>
          )}
          {/* end::Search */}
        </div>

        {/* begin::Card toolbar */}
        <div className='card-toolbar'>
          {/* begin::Group actions */}
          {IsShowSlectedFilter(props.selected, props.pageSetting?.totalRow ?? 0, props.isAllPageSelected) ? (
            <>

              <div className='d-flex justify-content-end align-items-center'>
                <div className='fw-bolder me-5'>
                  <span className='me-2'>{props.isAllPageSelected ? CalculateInvertSelectedCount(props.selected, props.pageSetting?.totalRow ?? 0, props.isAllPageSelected) : props.selected.length}</span> Selected
                  <span className='me-2'> <a href='#/' onClick={props.onSelectedAllItemPages} >| {props.isAllPageSelected ? 'clear all selected' : 'select all'} </a></span>
                </div>
                {props.toolbarSetting.customizeSelectionAction ? props.toolbarSetting.customizeSelectionAction?.(props.selected,props.isAllPageSelected) : <DefaultOnSelectDeleteButton />}
              </div>
            </>
          ) : (
            <Toolbar
              toolbarSetting={props.toolbarSetting}
              columns={props.columns}
              onApply={props.onApply}
              onAdd={props.onAdd}
              customizeAddButton={props.toolbarSetting.customizeAddButton}
            />
          )}
          {/* end::Group actions */}
        </div>
        {/* end::Card toolbar */}
      </div>
    </>
  )
}

const IsInvertSelected = (selected: any[], pkData: any, isAllPageSelected: boolean) => {
  return !isAllPageSelected ? selected.includes(pkData) : !selected.includes(pkData)
}

const IsShowSlectedFilter = (selected: any[], totalRow: number, isAllPageSelected: boolean) => {
  return !isAllPageSelected ? selected.length > 0 : totalRow - selected.length > 0
}

const CalculateInvertSelectedCount = (selected: any[], totalRow: number, isAllPageSelected: boolean) => {
  return !isAllPageSelected ? selected.length : totalRow - selected.length
}

export const TableStd = <T extends object>(props: Props<T, ColDef<T>> & typeof defaultProps) => {
  //const [pagination, setPagination] = useState(defaultPagination)
  const [isLoading, setIsLoading] = useState(false)
  const toolbarSetting = Object.assign({}, defaultProps.toolbarSetting, props.toolbarSetting)
  const tableSetting = Object.assign({}, defaultProps.tableSetting, props.tableSetting)
  const pageSetting = Object.assign({}, props.pageSetting)
  // const columns = useMemo(() => props.columns, [])
  // const colDeffooters = useMemo(() => props.footers, [])
  const columns = props.columns
  const colDeffooters = props.footers
  const [selected, setSelected] = useState<any[]>([])
  const [isAllSelected, setIsAllSelected] = useState(false)
  const [isAllPageSelected, setIsAllPageSelected] = useState(false)
  const data = props.data
  const dataFoot = props.dataFooter



  useEffect(() => {
    if (data.length === 0) return
    let cnt = 0;
    data.forEach((row: T, i) => {
      if (IsInvertSelected(selected, tableSetting.primaryKey(row), isAllPageSelected)) {
        cnt++;
      }
    })
    setIsAllSelected(data.length === cnt)
  }, [data, selected])

  useEffect(() => {
    setIsAllSelected(false)
    setIsAllPageSelected(false)
    setSelected([])
    console.log('forceClearSelected',selected,isAllPageSelected)
  }, [props.toolbarSetting.toolbarState?.forceClearSelected])

  const onSelect = (e: any, id: any) => {
    e.preventDefault()
    if (!id) {
      return
    }
    if (selected.includes(id)) {
      setSelected(selected.filter((itemId) => itemId !== id))
    } else {
      setSelected((state) => [...state, id])
    }
  }

  const onAllSelect = (e: any) => {
    e.preventDefault()
    const newSelected: any[] = []

    data.forEach((d, index) => {
      newSelected.push(tableSetting.primaryKey(d))
    })


    if (!isAllPageSelected) {
      const filterOut = selected.filter((itemId) => !newSelected.includes(itemId))
      if (e.target.checked) {
        setSelected(filterOut.concat(newSelected))
      } else {
        setSelected(filterOut)
      }
    } else {
      const filterOut = selected.filter((itemId) => !newSelected.includes(itemId))
      if (e.target.checked) {
        setSelected(filterOut)
      } else {

        setSelected(filterOut.concat(newSelected))
      }
    }
    setIsAllSelected(e.target.checked)

  }

  const onAddHandler = (e: any) => {
    props.toolbarSetting.onBtnAddClick?.(e)
  }
  const onSelectedAllItemPages = () => {
    setSelected([])
    setIsAllPageSelected(!isAllPageSelected)
    setIsAllSelected(!isAllPageSelected)
  }


  const onPageChage = (e: any, page: number) => {
    setSelected([])
    tableSetting.onPageChange?.(e, page)
  }

  const renderFooter = () => {

  }

  const Table = () => {
    const TBody = () => {
      return <tbody className='text-gray-600 fw-bold'>
        {data.length > 0 ? (
          data.map((row: T, i) => {
            return (
              <tr key={i}>
                {tableSetting.showSelection === true && (
                  <td>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={IsInvertSelected(selected, tableSetting.primaryKey(row), isAllPageSelected)}
                        onChange={(e) => onSelect(e, tableSetting.primaryKey(row))}
                      />
                    </div>
                  </td>
                )}
                {Object.entries(columns).map(([item, value], index) => {
                  if (value.hidden) return false
                  const component = value.renderItem?.({ item: row, index, opt: tableSetting.options, source: props.data,events:value.events  })
                  if (component) {
                    return <td key={index}>{component}</td>
                  } else {
                    return <td key={index}></td>
                  }
                })}
                {tableSetting.showColAction && (
                  <td className='text-center'>
                    <button
                      type='button'
                      onClick={(e) =>
                        tableSetting.onBtnEditClick?.(e, tableSetting.primaryKey(row))
                      }
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                    >
                      <KTSVG
                        path='/media/icons/duotune/art/art005.svg'
                        className='svg-icon-3'
                      />
                    </button>

                    <button
                      type='button'
                      onClick={(e) =>
                        tableSetting.onBtnDeleteClick?.(e, tableSetting.primaryKey(row))
                      }
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen027.svg'
                        className='svg-icon-3'
                      />
                    </button>
                  </td>
                )}
              </tr>
            )
          })
        ) : (
          <tr>
            <td colSpan={columns.length}>
              <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                No matching records found
              </div>
            </td>
          </tr>
        )}
      </tbody>
    }

    return (
      <KTCardBody className='py-4'>
        <div className='table-responsive'>
          <table className='table align-middle table-bordered fs-6 gy-5 dataTable'>
            <thead>
              <tr className='table-light text-start fw-bolder fs-4 text-uppercase gs-0'>
                {tableSetting.showSelection === true && (
                  <th>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={isAllSelected}
                        onChange={(e) => onAllSelect(e)}
                      />
                    </div>
                  </th>
                )}
                {columns.map((column: ColDef<T>, index) => {
                  if (column.hidden) return false
                  return (
                    <th key={index} style={{ width: column.width }}>
                      {column.label}
                    </th>
                  )
                })}
                {tableSetting.showColAction && <th className='text-center'>
                  Action
                </th>}
              </tr>
            </thead>
            {props.isLoading && props.isLoading === true ? LoadingComponent() : TBody()}
            {dataFoot && <tfoot >
              {dataFoot?.length > 0 ? (dataFoot.map((rowFoot, index) =>
                <tr key={index} className="table-secondary fw-bolder fs-6 text-uppercase gs-0">
                  {colDeffooters && Object.entries(colDeffooters).map(([item, value], index) => {
                    if (value.hidden) return false
                    const component = value.renderItem?.({ item: rowFoot, index, opt: tableSetting.options, source: props.data,events:value.events })
                    if (component) {
                      return <td key={index} >{component}</td>
                    } else {
                      return <td key={index}></td>
                    }
                  })}
                </tr>)) : <></>}
            </tfoot>}
          </table>
        </div>

        {props.pageSetting && <PaginationStd
          pageSetting={props.pageSetting}
          onPageChange={(e: any, page: number) => onPageChage(e, page)}
          isLoading={isLoading}
        />}
      </KTCardBody>
    )
  }

  return (
    <>
      <KTCard>
        <Header
          toolbarSetting={toolbarSetting}
          pageSetting={pageSetting}
          columns={props.columns}
          isAllPageSelected={isAllPageSelected}
          onApply={(val: any[]) => props.toolbarSetting?.onFilterApply?.(val)}
          onAdd={onAddHandler}
          onSelectedAllItemPages={onSelectedAllItemPages}
          selected={selected}
          onDeleteSelected={(val: any[]) => {
            props.toolbarSetting?.onDeleteSelected?.(val)
            setSelected([])
          }}
        />
        <Table />
      </KTCard>
    </>
  )
}

TableStd.defaultProps = defaultProps
